import { Skeleton } from '@/components/ui/skeleton';
import {
    MonitoringSite,
    SettingsSample,
    useMonitoringSamples,
    useSetMonitoring,
} from '@/services/field.service';
import BackofficePage, { DataCard } from '../../components/BackofficePage';
import MonitoringTabs from './MonitoringTabs';

/**
 * Where a sample can be sent, in the order the buttons sit. `null` is off both
 * pages, which is where most samples stay.
 */
const CHOICES: { value: MonitoringSite | null; label: string }[] = [
    { value: null, label: 'Off' },
    { value: 'FIELD', label: 'Field' },
    { value: 'BENCH', label: 'Bench' },
];

export default function BackOfficeMonitoringSettings() {
    const { data: projects, isLoading, error } = useMonitoringSamples();
    const setMonitoring = useSetMonitoring();

    if (isLoading)
        return (
            <Frame>
                <Skeleton className="w-full h-[400px] bg-[#1e242a]" />
            </Frame>
        );
    if (error)
        return (
            <Frame>
                <div className="text-red-400 text-sm">{error.toString()}</div>
            </Frame>
        );

    const groups = projects?.data ?? [];
    const counts = groups
        .flatMap((g) => g.samples)
        .reduce(
            (acc, s) => {
                if (s.monitoring === 'FIELD') acc.field++;
                else if (s.monitoring === 'BENCH') acc.bench++;
                return acc;
            },
            { field: 0, bench: 0 },
        );

    return (
        <Frame>
            <div className="text-xs text-[#6b7480] mb-4">
                {counts.field} in the field · {counts.bench} on the bench
            </div>
            <div className="space-y-6">
                {groups.map((group) => (
                    <DataCard key={group.projectId}>
                        <div className="px-4 py-3 border-b border-[#1e242a] text-sm font-medium text-white">
                            {group.projectName}
                        </div>
                        <div>
                            {group.samples.map((sample) => (
                                <SampleRow
                                    key={sample.id}
                                    sample={sample}
                                    pending={
                                        setMonitoring.isPending &&
                                        setMonitoring.variables?.sampleId ===
                                            sample.id
                                    }
                                    onPick={(monitoring) =>
                                        setMonitoring.mutate({
                                            sampleId: sample.id,
                                            monitoring,
                                        })
                                    }
                                />
                            ))}
                        </div>
                    </DataCard>
                ))}
                {groups.length === 0 && (
                    <div className="text-sm text-[#6b7480]">
                        No samples found.
                    </div>
                )}
            </div>
        </Frame>
    );
}

function Frame({ children }: { children: React.ReactNode }) {
    return (
        <BackofficePage
            title="Monitoring"
            description="Send each sample to the Field page, the Bench page, or neither. Field is a device planted out on a real site; Bench is one kept at the office for development or pre-production testing."
        >
            <MonitoringTabs />
            {children}
        </BackofficePage>
    );
}

/** One sample and the three places it can go, as a segmented control. */
function SampleRow({
    sample,
    pending,
    onPick,
}: {
    sample: SettingsSample;
    pending: boolean;
    onPick: (monitoring: MonitoringSite | null) => void;
}) {
    return (
        <div
            className={`w-full flex items-center justify-between gap-4 px-4 py-2.5 border-b border-[#1e242a] last:border-b-0 hover:bg-[#1a2126] ${
                pending ? 'opacity-50' : ''
            }`}
        >
            <span className="text-sm text-[#d8dde2] truncate">
                {sample.name}
            </span>
            <div className="flex flex-shrink-0 rounded border border-[#2d3640] overflow-hidden">
                {CHOICES.map((choice) => {
                    const active = sample.monitoring === choice.value;
                    return (
                        <button
                            key={choice.label}
                            type="button"
                            disabled={pending || active}
                            onClick={() => onPick(choice.value)}
                            className={`px-3 py-1 text-xs transition border-r border-[#2d3640] last:border-r-0 ${
                                active
                                    ? 'bg-[#74c69d] text-[#0a0e12] font-medium'
                                    : 'text-[#a8b0bb] hover:bg-[#252e37]'
                            }`}
                        >
                            {choice.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
